import { UserRole } from '../types/user';
import { LifeArea } from './integratedProfileGenerator';

export type ConsumerPlan =
  | 'GUEST'
  | 'BASIC'
  | 'PREMIUM'
  | 'PRO_ASTROLOGER'
  | 'ULTRA_PREMIUM';

export type BillingCycle =
  | 'ONE_TIME'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'SIX_MONTH'
  | 'YEARLY'
  | 'LIFETIME'
  | 'ADD_ON_CREDITS'
  | 'PROMOTIONAL';

export interface PlanEntitlementConfig {
  planId: ConsumerPlan;
  displayName: string;
  billingCycle: BillingCycle;
  basePriceInr: number;
  aiQuestionsPerHour: number;
  monthlyAiQuestionsQuota: number;
  canExportPdf: boolean;
  canPrintReport: boolean;
  canAccessAstrologerCore: boolean;
  canAccessDeepDives: boolean;
  canAccessD1ToD60Charts: boolean;
  canAccessSpecializedLagnas: boolean;
  canAccessMuhurtaCalculator: boolean;
  canAccessGunMilan: boolean;
  canBookConsultation: boolean;
  canAccessAdvancedLifeAnalysis: boolean;
  canAccessLearnerMode: boolean;
  canAccessDynamicTiming: boolean;
  maxStoredKundalis: number;
}

export const PLAN_CONFIGS: Record<ConsumerPlan, PlanEntitlementConfig> = {
  GUEST: {
    planId: 'GUEST',
    displayName: 'Guest Pass (Preview)',
    billingCycle: 'ONE_TIME',
    basePriceInr: 0,
    aiQuestionsPerHour: 80,
    monthlyAiQuestionsQuota: 20,
    canExportPdf: false,
    canPrintReport: false,
    canAccessAstrologerCore: false,
    canAccessDeepDives: false,
    canAccessD1ToD60Charts: false,
    canAccessSpecializedLagnas: false,
    canAccessMuhurtaCalculator: false,
    canAccessGunMilan: true,
    canBookConsultation: true,
    canAccessAdvancedLifeAnalysis: true, // Preview enabled
    canAccessLearnerMode: true,
    canAccessDynamicTiming: true,
    maxStoredKundalis: 0
  },
  BASIC: {
    planId: 'BASIC',
    displayName: 'Basic Member',
    billingCycle: 'MONTHLY',
    basePriceInr: 299,
    aiQuestionsPerHour: 80,
    monthlyAiQuestionsQuota: 80,
    canExportPdf: true,
    canPrintReport: true,
    canAccessAstrologerCore: false,
    canAccessDeepDives: true,
    canAccessD1ToD60Charts: false,
    canAccessSpecializedLagnas: false,
    canAccessMuhurtaCalculator: true,
    canAccessGunMilan: true,
    canBookConsultation: true,
    canAccessAdvancedLifeAnalysis: true,
    canAccessLearnerMode: true,
    canAccessDynamicTiming: true,
    maxStoredKundalis: 10
  },
  PREMIUM: {
    planId: 'PREMIUM',
    displayName: 'Premium Jyotish Explorer',
    billingCycle: 'YEARLY',
    basePriceInr: 1499,
    aiQuestionsPerHour: 500,
    monthlyAiQuestionsQuota: 500,
    canExportPdf: true,
    canPrintReport: true,
    canAccessAstrologerCore: true,
    canAccessDeepDives: true,
    canAccessD1ToD60Charts: true,
    canAccessSpecializedLagnas: true,
    canAccessMuhurtaCalculator: true,
    canAccessGunMilan: true,
    canBookConsultation: true,
    canAccessAdvancedLifeAnalysis: true,
    canAccessLearnerMode: true,
    canAccessDynamicTiming: true,
    maxStoredKundalis: 100
  },
  PRO_ASTROLOGER: {
    planId: 'PRO_ASTROLOGER',
    displayName: 'Professional Astrologer Suite',
    billingCycle: 'YEARLY',
    basePriceInr: 4999,
    aiQuestionsPerHour: 2000,
    monthlyAiQuestionsQuota: 2000,
    canExportPdf: true,
    canPrintReport: true,
    canAccessAstrologerCore: true,
    canAccessDeepDives: true,
    canAccessD1ToD60Charts: true,
    canAccessSpecializedLagnas: true,
    canAccessMuhurtaCalculator: true,
    canAccessGunMilan: true,
    canBookConsultation: true,
    canAccessAdvancedLifeAnalysis: true,
    canAccessLearnerMode: true,
    canAccessDynamicTiming: true,
    maxStoredKundalis: 1000
  },
  ULTRA_PREMIUM: {
    planId: 'ULTRA_PREMIUM',
    displayName: 'Ultra Premium / Institutional',
    billingCycle: 'LIFETIME',
    basePriceInr: 9999,
    aiQuestionsPerHour: 9999,
    monthlyAiQuestionsQuota: 9999,
    canExportPdf: true,
    canPrintReport: true,
    canAccessAstrologerCore: true,
    canAccessDeepDives: true,
    canAccessD1ToD60Charts: true,
    canAccessSpecializedLagnas: true,
    canAccessMuhurtaCalculator: true,
    canAccessGunMilan: true,
    canBookConsultation: true,
    canAccessAdvancedLifeAnalysis: true,
    canAccessLearnerMode: true,
    canAccessDynamicTiming: true,
    maxStoredKundalis: 99999
  }
};

// ----------------------------------------------------------------------------
// Astrologer Consultation Architecture
// ----------------------------------------------------------------------------

export type ConsultationSlotState =
  | 'AVAILABLE'
  | 'HELD'
  | 'CONFIRMED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'EXPIRED';

export interface AstrologerProfile {
  id: string;
  name: string;
  title: string;
  experienceYears: number;
  specializations: LifeArea[];
  languages: string[];
  baseFee30MinInr: number;
  rating: number;
  reviewCount: number;
}

export interface ConsultationSlot {
  slotId: string;
  astrologerId: string;
  astrologerName: string;
  startTime: string; // ISO String
  endTime: string;   // ISO String (30 mins)
  state: ConsultationSlotState;
  holdExpiresAt?: string; // ISO String (e.g. 10 mins from hold)
}

export interface ConsultationBookingRequest {
  bookingId: string;
  userId: string;
  maskedUserName: string; // e.g. "R**** S****"
  maskedPhone?: string;   // e.g. "+91 98****1234"
  lifeTheme: LifeArea;
  specificQuestion: string;
  preferredLanguage: string;
  selectedSlot: ConsultationSlot;
  calculatedFeeInr: number;
  status: ConsultationSlotState;
  createdAt: string;
}

export interface PostConsultationSummary {
  bookingId: string;
  astrologerName: string;
  consultationDate: string;
  lifeArea: LifeArea;
  keyInsights: string[];
  recommendedRemedies: string[];
  followUpWindowDays: number;
}

export const SAMPLE_ASTROLOGERS: AstrologerProfile[] = [
  {
    id: 'ASTRO_01',
    name: 'Acharya Vidyadhar Shastri',
    title: 'Senior Parashari & Vastu Scholar',
    experienceYears: 24,
    specializations: ['career', 'wealth', 'property'],
    languages: ['Hindi', 'English', 'Sanskrit'],
    baseFee30MinInr: 1100,
    rating: 4.95,
    reviewCount: 412
  },
  {
    id: 'ASTRO_02',
    name: 'Dr. Sunita Sharma',
    title: 'Ph.D. in Vedic Jyotish & Matchmaking Specialist',
    experienceYears: 18,
    specializations: ['marriage', 'progeny', 'education'],
    languages: ['Hindi', 'English'],
    baseFee30MinInr: 950,
    rating: 4.92,
    reviewCount: 328
  },
  {
    id: 'ASTRO_03',
    name: 'Pandit Radheshyam Joshi',
    title: 'KP System & Medical Astro Specialist',
    experienceYears: 21,
    specializations: ['health', 'foreignTravel', 'spirituality'],
    languages: ['Hindi', 'Gujarati', 'English'],
    baseFee30MinInr: 1250,
    rating: 4.98,
    reviewCount: 520
  }
];

export function maskNativeIdentity(fullName: string): string {
  if (!fullName) return 'Native';
  const parts = fullName.trim().split(' ');
  return parts
    .map(p => (p.length > 2 ? `${p[0]}${'*'.repeat(p.length - 2)}${p[p.length - 1]}` : `${p[0]}*`))
    .join(' ');
}

export function calculateConsultationFee(
  baseFee: number,
  plan: ConsumerPlan
): { originalFee: number; discountInr: number; finalFeeInr: number } {
  let discountPct = 0;
  if (plan === 'PREMIUM') discountPct = 15;
  if (plan === 'PRO_ASTROLOGER' || plan === 'ULTRA_PREMIUM') discountPct = 25;

  const discountInr = Math.round((baseFee * discountPct) / 100);
  const finalFeeInr = baseFee - discountInr;

  return {
    originalFee: baseFee,
    discountInr,
    finalFeeInr
  };
}
